import { DefectReportEntryDto, RingDetailsDto } from 'generated/apiSchemas';
import {
  DefectReport,
  DefectReportType,
  RepairReport,
  Ring,
  SegmentDrawConfig,
  TunnelPosition,
} from 'technical/db';

import {
  BoardDisplayMode,
  Point,
  RingData,
  SegmentData,
  SegmentShapeType,
} from './types';

const MARGIN_CANVAS_X = 10;
const MARGIN_CANVAS_Y = 25;
const MARGIN_BOARD_RIGHT = 10;

const BOARD_TEXT_SIZE = 40;

const SEGMENT_HEIGHT = 40;

const TILT_VALUE = 10;
const EXTENDED_TILT_VALUE = 20;

const RING_BUTTONS_SIZE = 100;
const DEFAULT_COLOR = '#888888';
export const DEFAULT_DRAW_COLOR = '#ff0000';

const ENTRY_HOUR_LINES = '12h,9h,6h,3h,12h';
const EXIT_HOUR_LINES = '12h,3h,6h,9h,12h';

export enum BoardViewMode {
  Entry = 'entry',
  Exit = 'exit',
}

// we have 2 types of data to display a board:
// boardData contains information about the arrangement of the segments with:
//     - the segment key
//     - the order of the segments on the first ring position
//     - the number of segments per ring
//     - the display shape of the segment depending on its type
// ringsData contains the data for the ring with:
//     - the list of associated segments
//     - the ring number
//     - the ring position type

export const formatBoardSegments = (
  segmentDrawConfigs: SegmentDrawConfig[],
  tunnelPositions: TunnelPosition[],
  rawRings: Ring[] | RingDetailsDto[],
  defectReports: DefectReport[] | DefectReportEntryDto[],
  defectReportTypes: DefectReportType[],
  repairReports: RepairReport[] = [],
) => {
  const rings: RingData[] = [];
  rawRings.forEach((ringData) => {
    const boardSegments: SegmentData[] = [];
    const tunnelPosition = tunnelPositions.find(
      (position) => position.keyPosition === ringData.keyPosition,
    );
    const ringDefectReports = defectReports.filter(
      (defect) =>
        defect.ringId === ringData.id &&
        !('deletedAt' in defect && defect.deletedAt),
    );

    segmentDrawConfigs.forEach((segmentConfig) => {
      const segmentDefectReports = ringDefectReports.filter(
        (defect) => defect.segmentType === segmentConfig.label,
      );

      boardSegments.push({
        id: `${ringData.ringNumber}#%id%#${segmentConfig.label}`,
        key: `${ringData.ringNumber}-${segmentConfig.label}`,
        label: segmentConfig.label,
        shapeType: segmentConfig.shape as SegmentShapeType,
        defects: segmentDefectReports.map((defect) => {
          const defectType = defectReportTypes.find(
            (type) => type.id === defect.defectReportTypeId,
          );
          const repair = repairReports.find(
            (r) => r.defectReportId === defect.id,
          );
          return {
            id: defect.id,
            strokes: defect.strokes as Point[],
            color: defectType?.color ?? DEFAULT_COLOR,
            toRepair: defect.toRepair,
            submittedAt: defect.submittedAt,
            repairSubmittedAt: repair?.submittedAt,
          };
        }),
      });
    });

    rings.push({
      id: ringData.id,
      label: ringData.ringNumber.toString(),
      type: `P${ringData.keyPosition}`,
      marginX: tunnelPosition?.positionX ?? 0,
      compliantDate: ringData.compliantDate,
      segments: boardSegments,
      hasKeyPosition: !!ringData.keyPosition,
    });
  });

  return rings;
};

export const getBoardConfiguration = (
  segmentConfigs: {
    id: string;
    label: string;
    order: number;
    shape: string;
  }[],
  segmentWidth: number,
  nbRings: number,
  displayMode: BoardDisplayMode,
) => {
  if (!segmentWidth) {
    return null;
  }

  const nbSegments = segmentConfigs.length;

  const segmentKey = segmentConfigs.find(
    (segment) => segment.shape === SegmentShapeType.Key,
  );

  const leftMargin = [
    BoardDisplayMode.Complete,
    BoardDisplayMode.Preview,
  ].includes(displayMode)
    ? MARGIN_CANVAS_X + BOARD_TEXT_SIZE
    : 0;

  // calculate the max width of the board, first only segments, then add margins
  const ringSegmentsWidth = nbSegments * segmentWidth;
  const leftMarginInsideBoard = segmentWidth / 2 + TILT_VALUE;
  const ringBoardWidth = ringSegmentsWidth + 2 * leftMarginInsideBoard; // we add 1 segment and space for tilts
  const boardWidth = leftMargin + ringBoardWidth + RING_BUTTONS_SIZE;

  // calculate the max heigth of the board (+ 2*margin)
  const boardHeight = nbRings * SEGMENT_HEIGHT + MARGIN_CANVAS_Y * 2;

  const leftMarginHourLines =
    displayMode === BoardDisplayMode.Ring
      ? leftMarginInsideBoard
      : leftMargin + leftMarginInsideBoard;

  return {
    board: {
      width: boardWidth,
      height: boardHeight,
      displayMode: displayMode,
    },
    padding: {
      left: MARGIN_CANVAS_X,
      top: MARGIN_CANVAS_Y,
    },
    margin: {
      left: leftMargin,
      leftText: BOARD_TEXT_SIZE,
      leftHourLines: leftMarginHourLines,
      leftInsideBoard: leftMarginInsideBoard,
      rightInsideBoard: MARGIN_BOARD_RIGHT,
    },
    ringSegmentsWidth,
    ringBoardWidth,
    segment: {
      width: segmentWidth,
      height: SEGMENT_HEIGHT,
      keyLabel: segmentKey?.label ?? '',
      nbByRing: nbSegments,
      tilt: {
        value: TILT_VALUE,
        extendedValue: EXTENDED_TILT_VALUE,
      },
    },
  };
};

export const getHourLineLabels = (viewMode: BoardViewMode) => {
  const labels =
    viewMode === BoardViewMode.Entry ? ENTRY_HOUR_LINES : EXIT_HOUR_LINES;
  return labels.split(',');
};
